/**
 * IssueItemForm Component
 * Form for issuing existing inventory items
 */

import type { InventoryItem } from '@/types/index.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';

interface IssueItemFormProps {
  selectedItem: InventoryItem;
  quantity: number | '';
  onQuantityChange: (value: number | '') => void;
  itemsPerUnit: number | '';
  onItemsPerUnitChange: (value: number | '') => void;
  /** Stock available in the currently selected source location (overrides global quantity) */
  locationAvailable?: number;
  onClear?: () => void;
}

export default function IssueItemForm({
  selectedItem,
  quantity,
  onQuantityChange,
  itemsPerUnit,
  onItemsPerUnitChange,
  locationAvailable,
  onClear,
}: IssueItemFormProps) {
  const currentUnit = selectedItem.unit || 'units';
  const needsItemsPerUnit = requiresItemsPerUnit(currentUnit);
  const availableQuantity = locationAvailable !== undefined ? locationAvailable : (selectedItem.quantity || 0);

  const handleQuantityChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? '' : Number(value);
      if (numValue === '' || (typeof numValue === 'number' && numValue > 0 && numValue <= availableQuantity)) {
        onQuantityChange(numValue);
      }
    }
  };

  const handleItemsPerUnitChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      onItemsPerUnitChange(value === '' ? '' : Number(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    if (
      [46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if (
      (e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Item Info Display */}
      <div className="p-4 bg-[#84d0f4]/10 rounded-lg relative">
        {onClear && (
          <button
            onClick={onClear}
            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
            title="Clear item"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}
        <p className="text-sm font-medium text-gray-900 pr-6">{selectedItem.name}</p>
        {selectedItem.sku && <p className="text-xs text-gray-600 mt-1">SKU: {selectedItem.sku}</p>}
        <p className="text-xs text-gray-600">Barcode: {selectedItem.barcode}</p>
        <p className="text-xs text-gray-600">
          Available:{' '}
          <span className={availableQuantity === 0 ? 'text-red-600 font-medium' : 'text-gray-700 font-medium'}>
            {availableQuantity} {currentUnit}
          </span>
          {locationAvailable !== undefined && (
            <span className="ml-1 text-gray-400">(in selected location)</span>
          )}
        </p>
      </div>

      {/* Quantity Fields - Conditionally rendered based on unit */}
      {needsItemsPerUnit ? (
        <>
          {/* Number of boxes/packs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of {currentUnit} <span className="text-[#ed1438]">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={handleKeyDown}
              placeholder={`Enter number of ${currentUnit}`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum: {availableQuantity} {currentUnit} available
            </p>
          </div>

          {/* Items per boxes/packs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Items per {currentUnit} <span className="text-[#ed1438]">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={itemsPerUnit}
              onChange={(e) => handleItemsPerUnitChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={handleKeyDown}
              //placeholder={`How many items in each ${currentUnit}?`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Total quantity will be:{' '}
              {quantity !== '' && itemsPerUnit !== ''
                ? `${quantity} × ${itemsPerUnit} = ${
                    (typeof quantity === 'number' ? quantity : Number(quantity)) *
                    (typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit))
                  }`
                : 'Quantity × Items per unit'}
            </p>
          </div>
        </>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span className="text-[#ed1438]">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={handleKeyDown}
            placeholder="Enter quantity"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum: {availableQuantity} {currentUnit} available
          </p>
        </div>
      )}
    </div>
  );
}

